<template>
<div>
  <b-modal
    ref="select-course-modal"
    v-if="featureSet.course_selection.control == 'button' && hasMultipleRoutes"
    title="Select course"
    hide-header
    hide-footer
  >
    <h5>Select course:</h5>
    <b-form-select
      :value="route.id"
      :options="selectableRoutes"
      size="lg"
      text-field="name"
      value-field="id"
      @change="handleCourseSelection"
    ></b-form-select>
  </b-modal>

  <map-header
    v-if="showInfoHeader"
    :name="meta.name"
    :sourcePath="meta.source_path"
    :distance="route.distance"
    :elevationGain="route.elevation_gain"
    :elevationLoss="route.elevation_loss"
    :elevationHigh="route.elevation_high"
    :elevationLow="route.elevation_low"
    :metric="metric"
  ></map-header>

  <div
    v-if="routeHoverDetails"
    class="ml-2"
    style="position: absolute; z-index: 100; top: calc(40vh)"
  >
    <b-card text-variant="white" bg-variant="dark">
      <b-card-text>
        <span class="font-weight-bold">
          {{routeHoverDetails.header}}
        </span>
        <span>
          {{routeHoverDetails.message}}
        </span>
        </b-card-text>
    </b-card>
  </div>

  <div id="map"></div>

  <legend-pane
    v-if="routeLineDetailsPresent"
    :map-style="style"
    class="ml-2"
    style="position: absolute; z-index: 100; top: calc(50vh)"
  ></legend-pane>

  <div @mouseleave="handleElevationChartMouseLeave">
    <elevation-annotation
      v-if="elevationChartInteractionValues"
      :distance="elevationChartInteractionValues.distance"
      :elevation="elevationChartInteractionValues.elevation"
      :grade="elevationChartInteractionValues.grade"
      style="z-index: 10;"
    />

    <div
      v-if="elevationChartInteractionValues"
      v-bind:style="elevationChartBarPosition"
    ></div>

    <elevation-chart
      :routeId="route.id"
      :options="chartOptions"
      :point-features="pointFeatures"
      @chart-canvas-mousemove="handleElevationChartMouseMove"
    />
  </div>
</div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { featureCollection, point } from '@turf/turf';
import { BModal, BFormSelect, BCard, BCardText } from 'bootstrap-vue';
import { isNil, isEmpty, remove, lowerCase, round, get, every, merge } from 'lodash';
import axios from 'axios';

import LegendPane from './legend_pane';
import ElevationAnnotation from './elevation_annotation';
import ElevationChart from './elevation_chart';
import MapHeader from './map_header';
import { buildRouteLayerEntities, RouteLayerManager } from './widgets/route_layer_manager';
import MapMarkerSet from './widgets/map_marker_set';
import DistancePopupSet from './widgets/distance_popup_set';
import ElevationPositionMarker from './widgets/elevation_position_marker';
import ElevationPolygonSet from './widgets/elevation_polygon_set';
import Terrain3d from './widgets/terrain_3d';
import BasicControl from './controls/basic_control';
import CourseDataControl from './controls/course_data_control';
import StyleControl from './controls/style_control';
import SelectControl from './controls/select_control';
import GpxDownloadControl from './controls/gpx_download_control';
import {
  polylineToLineStringFeature,
  compactLineStringsByProperties
} from 'utilities/geojson_adapters';
import { camelCaseKeys } from 'utilities/object_helpers';
import unitConversion from "utilities/unit_conversion";
import mapboxGlSupported from '@mapbox/mapbox-gl-supported';

export default {
  components: {
    ElevationAnnotation,
    ElevationChart,
    MapHeader,
    BModal,
    BFormSelect,
    BCard,
    BCardText,
    LegendPane
  },

  props: ['initData'],

  data() {
    let opts = JSON.parse(this.initData);
    const style = opts.styles.shift();
    const alternateStyles = opts.styles;

    return {
      assets: opts.assets,
      browser: opts.browser,
      metric: opts.metric,
      accessToken: opts.mapbox_token,
      route: opts.route,
      selectableRoutes: opts.selectable_routes,
      geoJSON: featureCollection([]),
      featureSet: opts.feature_set,
      controls: {
        courseDataControl: null,
        navigationControl: new mapboxgl.NavigationControl(),
        fullscreenControl: new mapboxgl.FullscreenControl(),
        geolocationControl: new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true
          },
          trackUserLocation: true,
          showUserHeading: true
        }),
        styleControl: null,
        courseSelectionControl: null
      },
      layerWidgets: {
        routeLayerManager: null,
        mapMarkerSet: null,
        elevationPositionMarker: null,
        elevationPolygonSet: null,
        distancePopups: null,
        terrain3d: null
      },
      map: null,
      routeHoverDetails: null,
      meta: opts.meta,
      chartOptions: {
        metric: opts.metric,
        axes: {
          x: {
            orientation: get(opts, 'feature_set.elevation_chart.x_axis.orientation')
          }
        },
        text: {
          distanceUnits: opts.metric ? "km" : "mi",
          elevationUnits: opts.metric ? "m" : "ft",
          xAxis: "Distance",
          yAxis: "Elevation"
        },
        elevationDisplay: camelCaseKeys(style.chart)
      },
      elevationChartBarPosition: null,
      elevationChartInteractionValues: null,
      alternateStyles: alternateStyles,
      style: style,
      zoomForCourseBounds: null
    };
  },

  computed: {
    bounds() {
      const coordinates = this.entireCourseAsLineStringFeature.geometry.coordinates;
      const result = coordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

      return result;
    },

    downloadGpxFileName() {
      if (this.meta) return this.meta.name;
      return "hellodrifter_course";
    },

    elevationPolygonDataEndpoint() {
      const endpoint = this.browser.mobile
        ? "hyper_compressed_elevation_data"
        : "compressed_elevation_data";

      return `/api/routes/${this.route.id}/${endpoint}`;
    },

    hasMultipleRoutes() {
      return this.selectableRoutes && this.selectableRoutes.length > 1;
    },

    hasMultipleStyles() {
      return this.alternateStyles && this.alternateStyles.length;
    },

    isAnimating() {
      return this.layerWidgets.routeLayerManager && this.layerWidgets.routeLayerManager.isAnimating();
    },

    entireCourseAsLineStringFeature() {
      return polylineToLineStringFeature(this.route.polyline);
    },

    pointFeatures() {
      return this.geoJSON.features.filter(feature => feature.geometry.type == "Point");
    },

    routeLineDetailsPresent() {
      return get(this, 'layerWidgets.routeLayerManager.line.details') == 'on'
    },

    selectableRouteOptions() {
      return this.selectableRoutes.map((route) => ({ value: route.id, text: route.name }));
    },

    showInfoHeader() {
      return this.featureSet.info_banner.enabled && !isNil(this.meta);
    },

    lineStringFeatures() {
      return this.geoJSON.features.filter(feature => feature.geometry.type == "LineString");
    },

    geojsonHasDetailInformation() {
      return every(this.lineStringFeatures, (feature) => {
        const properties = feature.properties || {};
        const { use, surface } = properties;

        return use && surface;
      });
    }
  },

  async mounted() {
    if (mapboxGlSupported.supported()) {
      mapboxgl.accessToken = this.accessToken;
      const initOptions = {
        container: 'map',
        style: this.assets.map_style_urls[this.style.map.tile_style],
        pitch: 0,
        attributionControl: false
      };

      if (!this.featureSet.map.fly_to_course_on_load.enabled) initOptions.bounds = this.bounds;

      this.map = new mapboxgl.Map(initOptions);
      this.map.addControl(new mapboxgl.AttributionControl({
        customAttribution: `
          <a href="https://www.hellodrifter.com">
            <span class="font-italic font-weight-bold">Powered by</span>
            <span class="font-weight-bold text-monospace">HelloDrifter.com</span>
          </a>
        `
      }));
      this.map.on("load", this.initializeMap);
      this.map.on("style.changeinitiating", this.prepareForNewStyle);
      this.map.on("style.loadcomplete", this.reloadLayersWithNewStyle);

      this.sendGoogleAnalyticsEvent('load');
    } else {
      this.trackUsageEvent("map_usage.incompatible_browser");
      this.$toasted.show(
        "We detected that your browser version might not support ours maps. " +
        "If refreshing the page doesn't work, please update your software or try again " +
        "with a different browser."
      );
    }
  },

  methods: {
    clearRouteDependentData() {
      this.geoJSON = featureCollection([]);
      if (this.layerWidgets.mapMarkerSet) {
        this.layerWidgets.mapMarkerSet.remove();
        this.layerWidgets.mapMarkerSet = null;
      }
      this.layerWidgets.routeLayerManager.remove();
      this.layerWidgets.routeLayerManager = null;
      this.layerWidgets.elevationPolygonSet.remove();
      this.layerWidgets.elevationPolygonSet = null;
      this.layerWidgets.distancePopups.remove();
      this.map.removeControl(this.controls.courseDataControl);
      this.map.removeControl(this.controls.gpxDownloadControl);
    },

    async fetchPoints() {
      let points;
      if (
        get(this, 'featureSet.elevation_chart.points_of_interest.enabled') &&
        get(this, 'route.geojson_file_urls.points')
      ) {
        const response = await axios.get(this.route.geojson_file_urls.points);
        points = response.data.features;
      } else {
        const response = await this.$axios.get(`/api/routes/${this.route.id}/points`);
        points = response.data.points;
      }
      this.geoJSON.features.push(...points);
    },

    initializeMap() {
      this.trackUsageEvent('map_usage.load');

      if (this.hasMultipleRoutes) {
        if (this.featureSet.course_selection.control == 'select') {
          this.controls.courseSelectionControl = new SelectControl({
            title: 'Select an alternate course',
            selectOptions: this.selectableRoutes,
            textField: 'name',
            valueField: 'id',
            iconCss: 'fas fa-lg fa-route',
            value: this.route.id,
            onChange: (event) => {
              this.handleCourseSelection(event.target.value);
            }
          });
        } else {
          this.controls.courseSelectionControl = new BasicControl({
            title: 'View different course',
            buttonHtmlContent: '<i class="fas fa-lg fa-route"/>',
            onClick: () => {
              this.$refs['select-course-modal'].show();
            }
          });
        }
        this.map.addControl(this.controls.courseSelectionControl, 'top-left');
      }

      if (this.hasMultipleStyles) {
        this.controls.styleControl = new StyleControl({
          selectedStyle: JSON.parse(JSON.stringify(this.style)),
          alternateStyles: this.alternateStyles.map(
            style => JSON.parse(JSON.stringify(style))
          ),
          mapStyleUrls: this.assets.map_style_urls
        });
        this.map.addControl(this.controls.styleControl, 'top-left');
      }

      this.map.addControl(this.controls.navigationControl, 'top-right');

      if (this.featureSet.map.fullscreen.control) {
        this.map.addControl(this.controls.fullscreenControl, 'top-right');
      }

      if (this.featureSet.map.geolocation.control && this.browser.mobile) {
        this.map.addControl(this.controls.geolocationControl, 'top-right');
      }

      this.map.on('moveend', (event) => {
        if (event.calledFromLoadingSelectedRouteData) this.drawCourse(event);
      });

      this.layerWidgets.terrain3d = new Terrain3d({
        map: this.map,
        exaggeration: get(this, 'featureSet.map.terrain_3d.exaggeration') || 1
      });
      if (get(this, 'featureSet.map.terrain_3d.enabled')) {
        this.layerWidgets.terrain3d.show({ setPitch: false });
      }

      this.loadSelectedRouteData();
    },

    drawCourse(event) {
      const zoom = this.map.getZoom();
      this.zoomForCourseBounds = zoom;
      if (this.layerWidgets.elevationPolygonSet) {
        this.layerWidgets.elevationPolygonSet.setZoom(
          Math.floor(this.zoomForCourseBounds)
        );
      }

      if (this.featureSet.elevation_3d.show_on_load) {
        this.controls.courseDataControl.showMarkers();
        this.controls.courseDataControl.animateCourse({
          onDone: () => {
            setTimeout(() => {
              this.controls.courseDataControl._handleEle3dClick({
                onActivatedCallback: () => {
                  if(!event.routeLoadedFromCourseSelection) {
                    this.$toasted.show(
                      "3D Elevation enabled. To view course details, disable with highlighted chart button on right side.",
                      { duration: 5000 }
                    );
                  }
                }
              });
              this.setMapInteractions('enable');
              this.setControlInteractions('enable');
            }, 500);
          }
        });
      } else {
        this.setMapInteractions('enable');
        this.setControlInteractions('enable');
        this.controls.courseDataControl.showMarkers();
        this.controls.courseDataControl.animateCourse();
      }

      this.controls.courseDataControl.reconcileButtonStates();
    },

    async loadSelectedRouteData({ eventOptions } = {}) {
      await this.resolveCourseLineStringData();

      await this.fetchPoints();

      this.layerWidgets.distancePopups = new DistancePopupSet({
        map: this.map,
        encodedPolyline: this.entireCourseAsLineStringFeature.properties.encodedPolyline,
        distanceUnits: this.metric ? 'kilometers' : 'miles',
        showOnLoad: this.featureSet.distance_markers.show_on_load,
        interval: this.featureSet.distance_markers.interval
      });

      if (!isEmpty(this.pointFeatures)) {
        this.layerWidgets.mapMarkerSet = new MapMarkerSet(this.pointFeatures, {
          map: this.map,
          assets: this.assets,
          style: this.style.map.layers.points,
          pointFeatureSet: this.featureSet.points
        });
      }
      this.layerWidgets.routeLayerManager = new RouteLayerManager(
        buildRouteLayerEntities({
          featureCollection: this.geoJSON,
          map: this.map,
          style: this.style.map.layers.route,
          detailsCallback: this.handleRouteLayerHover,
          animationTime: this.featureSet.route.animation.completion_time_on_load
        })
      );
      if (
        !this.browser.mobile &&
        this.featureSet.route.details.show_on_load &&
        get(this, 'route.geojson_file_urls.detailed_linestring')
      ) {
        this.layerWidgets.routeLayerManager.line.details = 'on';
      }
      this.layerWidgets.elevationPolygonSet = new ElevationPolygonSet(async () => {
        try {
          this.trackUsageEvent("map_usage.3d_elevation");

          const response = await this.$axios.get(this.elevationPolygonDataEndpoint);
          const featureColl = response.data["compressed_elevation_data"] ||
                              response.data["hyper_compressed_elevation_data"];
          return featureColl.features[0];
        } catch(error) {
          this.handleRequestError(error);
        }
      }, {
        map: this.map,
        bounds: this.bounds,
        style: this.style.map.layers.elevation_polygons,
        metric: this.metric,
        zoom: this.zoomForCourseBounds
      });

      this.controls.courseDataControl = new CourseDataControl(this.courseDataControlParams());

      if(get(this, 'featureSet.export.control')) {
        this.controls.gpxDownloadControl = new GpxDownloadControl({
          axios: this.$axios,
          routeId: this.route.id,
          onGpxUrlRetrieved: (url) => {
            this.$toasted.show("Downloading GPX file...", { duration: 3000 });
            fetch(url, { headers: { 'Origin': window.location } })
              .then(response => response.blob())
              .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${this.downloadGpxFileName}.gpx`);
                document.body.appendChild(link);
                link.click();

                this.trackUsageEvent('export.gpx');
              });
          },
          onGpxUrlNotRetrieved: (error) => {
            let message = "A GPX file with elevation is currently being generated for this course. " +
                          "This can take a few minutes. Please try again later."
            if (!error.request || error.request.status != 404) {
              message = "There was an unexpected error retrieving the GPX file; try refreshing your browser.";
              this.$sentry.captureException(error);
            }
            this.$toasted.show(message, { duration: 5000 });
          }
        });
      }

      this.map.addControl(this.controls.courseDataControl, 'top-right');
      if (this.controls.gpxDownloadControl) {
        this.map.addControl(this.controls.gpxDownloadControl, 'top-right');
      }

      this.layerWidgets.routeLayerManager.outline && this.layerWidgets.routeLayerManager.outline.show();
      this.layerWidgets.routeLayerManager.startMarker.show();
      this.setMapInteractions('disable');
      this.setControlInteractions('disable');

      this.controls.courseDataControl.reconcileButtonStates();

      // Don't start centering map on the course until all the widgets etc. are
      // loaded, since map zooming can end prior to these being completed!
      this.zoomForCourseBounds = null;
      this.map.fitBounds(
        this.bounds,
        {
          padding: 40,
          bearing: this.featureSet.map.default_bearing,
          pitch: this.featureSet.map.default_pitch
        },
        merge({}, { calledFromLoadingSelectedRouteData: true }, eventOptions || {})
      );
    },

    async resolveCourseLineStringData() {
      if (
        !this.browser.mobile &&
        get(this, 'route.geojson_file_urls.detailed_linestring') &&
        (this.featureSet.route.details.control || this.featureSet.route.details.show_on_load)
      ) {
        const response = await axios.get(this.route.geojson_file_urls.detailed_linestring);
        this.geoJSON = compactLineStringsByProperties(
          response.data,
          ["surface", "use", "unpaved"]
        );
      } else {
        this.geoJSON.features.push(this.entireCourseAsLineStringFeature);
      }
    },

    courseDataControlParams() {
      let ele3dCount = 0;
      let terrain3dCount = 0;
      let surfaceDetailsCount = 0;
      const params = {
        markerToggle: { widget: this.layerWidgets.mapMarkerSet },
        replayAnimation: {
          widget: this.layerWidgets.routeLayerManager,
          animationTime: this.featureSet.route.animation.completion_time_on_replay
        },
        distanceMarkerToggle: { widget: this.layerWidgets.distancePopups }
      }

      if (get(this.featureSet, 'elevation_3d.control')) params.ele3d = {
        innerHTML: '<i class="fa fa-lg fa-chart-area"/>',
        widget: this.layerWidgets.elevationPolygonSet,
        onActivatedCallback: () => {
          if (this.isMobileScreenSize() || ele3dCount > 0) return;

          ele3dCount += 1;
          this.$toasted.show('Hold down CTRL while dragging map to rotate', {
            duration: 3000
          });
        }
      };

      if (get(this.featureSet, 'map.terrain_3d.control')) params.terrain3dToggle = {
        widget: this.layerWidgets.terrain3d,
        onActivatedCallback: () => {
          if (terrain3dCount == 0) this.trackUsageEvent("map_usage.terrain_3d");
          if (this.isMobileScreenSize() || terrain3dCount > 0) return;

          terrain3dCount += 1;
          this.$toasted.show(
            'Hold down CTRL while dragging map to explore elevated terrain',
            { duration: 3000 }
          );
        }
      }

      if (this.geojsonHasDetailInformation && get(this.featureSet, 'route.details.control')) {
        params.routeDetailsToggle = {
          widget: this.layerWidgets.routeLayerManager,
          onActivatedCallback: () => {
            if (surfaceDetailsCount == 0) this.trackUsageEvent("map_usage.surface_details");
            if (surfaceDetailsCount > 0) return;

            surfaceDetailsCount += 1;
            this.$toasted.show(
              'Hover over sections of the course to see surface details',
              { duration: 4000 }
            );
          }
        };
      }

      return params;
    },

    isMobileScreenSize() {
      return window.innerWidth < 640;
    },

    setMapInteractions(status) {
      const interactionNames = [
        'scrollZoom',
        'boxZoom',
        'dragRotate',
        'dragPan',
        'keyboard',
        'doubleClickZoom',
        'touchZoomRotate'
      ];

      if (status === 'enable') {
        interactionNames.forEach((interactionName) => {
          this.map[interactionName].enable();
        });
      } else if (status === 'disable') {
        interactionNames.forEach((interactionName) => {
          this.map[interactionName].disable();
        });
      } else {
        throw `Invalid argument ${status} received`;
      }
    },

    setControlInteractions(status) {
      const controlNames = Object.keys(this.controls);

      if (status === 'enable') {
        controlNames.forEach((controlName) => {
          if (!this.controls[controlName] || !this.controls[controlName].enable) return;

          this.controls[controlName].enable();
        });
      } else if (status === 'disable') {
        controlNames.forEach((controlName) => {
          if (!this.controls[controlName] || !this.controls[controlName].disable) return;

          this.controls[controlName].disable();
        });
      } else {
        throw `Invalid argument ${status} received`;
      }
    },

    handleRouteLayerHover({ event, feature, layerId }) {
      if (event.type == "mouseenter") {
        const { distance, surface, use } = feature.properties
        const units = this.metric ? 'km' : 'mi'
        const convertedDistance = unitConversion(distance, { from: 'km', to: units });
        this.routeHoverDetails = {
          feature: feature,
          layerId,
          header: lowerCase(use),
          message: `${round(convertedDistance, 2)} ${units} on ${lowerCase(surface)}`
        }
      } else if (
        event.type == "mouseleave" &&
        (this.routeHoverDetails && layerId == this.routeHoverDetails.layerId)
      ) {
        this.routeHoverDetails = null;
      }
    },

    async handleCourseSelection(id) {
      try {
        const response = await this.$axios.get(`/api/routes/${id}/`);
        this.route = response.data.route;
        this.$refs['select-course-modal'] && this.$refs['select-course-modal'].hide();
        this.trackUsageEvent('map_usage.route_change');
        this.clearRouteDependentData();
        this.loadSelectedRouteData({ eventOptions: { routeLoadedFromCourseSelection: true } });
      } catch(error) {
        this.handleRequestError(error);
      }
    },

    handleElevationChartMouseLeave(data) {
      if (this.layerWidgets.elevationPositionMarker)
        this.layerWidgets.elevationPositionMarker.hide();

      this.elevationChartInteractionValues = null;
    },

    handleElevationChartMouseMove(data) {
      if (!this.map.isStyleLoaded()) return;

      if (this.layerWidgets.elevationPositionMarker == null) {
        this.layerWidgets.elevationPositionMarker = new ElevationPositionMarker(
          point(data.point),
          this.map
        );
      }

      this.layerWidgets.elevationPositionMarker.setPosition(point(data.point));
      this.layerWidgets.elevationPositionMarker.show();
      this.elevationChartInteractionValues = {
        distance: data.distance.text,
        elevation: data.elevation.text,
        grade: data.grade.text,
        x: data.mousemoveEvent.offsetX,
        graphBounds: data.graphBounds
      };
      this.elevationChartBarPosition = {
        position: "absolute",
        left: `${data.mousemoveEvent.offsetX}px`,
        height: `${Math.abs(data.graphBounds.y.max - data.graphBounds.y.min)}px`,
        width: "2px",
        backgroundColor: "black",
        "z-index": 10
      };
    },

    handleRequestError(error) {
      if (error.name === 'RefreshTokenExpiredError') {
        this.$toasted.show("Session expired. Refreshing map...", {
          duration: 3000,
          onComplete: () => location.reload()
        });
      } else if (!error.response) {
        throw error;
      } else {
        const message = `Received ${error.response.status} error ` +
                        `when requesting server data. Please try again later.`;
        this.$toasted.show(message, {
          duration: 8000
        });
      }
    },

    prepareForNewStyle(newStyle) {
      this.layerWidgets.terrain3d.hide();
      this.layerWidgets.elevationPolygonSet.hide({immediate: true});
      if (this.layerWidgets.mapMarkerSet) this.layerWidgets.mapMarkerSet.remove();
      this.layerWidgets.elevationPositionMarker = null;
      this.layerWidgets.routeLayerManager.remove();

      this.chartOptions = {
        metric: this.metric,
        axes: {
          x: {
            orientation: get(this.featureSet, 'elevation_chart.x_axis.orientation')
          }
        },
        text: {
          distanceUnits: this.metric ? "km" : "mi",
          elevationUnits: this.metric ? "m" : "ft",
          xAxis: "Distance",
          yAxis: "Elevation"
        },
        elevationDisplay: camelCaseKeys(newStyle.chart)
      };
    },

    reloadLayersWithNewStyle(newStyle) {
      remove(this.alternativeStyles, (style) => style.id == newStyle.id);
      this.alternateStyles.unshift(this.style);
      this.style = newStyle;
      this.layerWidgets.mapMarkerSet = new MapMarkerSet(this.pointFeatures, {
        map: this.map,
        assets: this.assets,
        style: newStyle.map.layers.points,
        pointFeatureSet: this.featureSet.points
      });
      this.controls.courseDataControl.setMarkerToggleWidget(this.layerWidgets.mapMarkerSet);
      this.layerWidgets.mapMarkerSet.show();

      const detailsStatus = this.layerWidgets.routeLayerManager.line.details;
      this.layerWidgets.routeLayerManager = new RouteLayerManager(
        buildRouteLayerEntities({
          featureCollection: this.geoJSON,
          map: this.map,
          style: this.style.map.layers.route,
          detailsCallback: this.handleRouteLayerHover,
          animationTime: this.featureSet.route.animation.completion_time_on_load
        })
      );
      this.layerWidgets.routeLayerManager.line.details = detailsStatus;

      this.controls.courseDataControl.setReplayAnimationWidget(
        this.layerWidgets.routeLayerManager
      );
      if (this.geojsonHasDetailInformation && get(this.featureSet, 'route.details.control')) {
        this.controls.courseDataControl.routeDetailsToggleWidget = this.layerWidgets.routeLayerManager;
      }
      this.layerWidgets.routeLayerManager.show();

      this.layerWidgets.elevationPolygonSet.style = newStyle.map.layers.elevation_polygons;
      this.layerWidgets.elevationPolygonSet.hide();
      if (get(this, 'featureSet.map.terrain_3d.enabled')) {
        this.layerWidgets.terrain3d.show();
      }

      this.controls.courseDataControl.reconcileButtonStates();
    },

    sendGoogleAnalyticsEvent(action) {
      if (!this.$gtag) return;

      this.$gtag("event", action, {
        event_category: "map_usage"
      });
    },

    trackUsageEvent(name) {
      this.$axios
        .post('/api/usage_events', { usage_event: { name: name } })
        .catch(error => this.$sentry.captureException(error));
    }
  }
};
</script>
